import React, { useState } from 'react';
import { X } from 'lucide-react';
import VehicleFormImages from './VehicleFormImages';
import VehicleFormOptions from './VehicleFormOptions';

const BasicInfoFields = ({ form, updateField }) => (
  <>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
      <input type="text" value={form.title} onChange={e => updateField('title', e.target.value)} required className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#DC2626]" placeholder="Ex: Peugeot 308 SW" data-testid="vehicle-title" />
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Prix *</label>
      <input type="text" inputMode="numeric" value={form.price} onChange={e => updateField('price', e.target.value.replace(/[^0-9]/g, ''))} required className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#DC2626]" placeholder="Ex: 8900" data-testid="vehicle-price" />
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Marque *</label>
      <input type="text" value={form.brand} onChange={e => updateField('brand', e.target.value)} required className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#DC2626]" placeholder="Ex: Peugeot" data-testid="vehicle-brand" />
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Modèle *</label>
      <input type="text" value={form.model} onChange={e => updateField('model', e.target.value)} required className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#DC2626]" placeholder="Ex: 308 SW" data-testid="vehicle-model" />
    </div>
  </>
);

const SpecsFields = ({ form, updateField }) => (
  <>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Année *</label>
      <input type="number" value={form.year} onChange={e => updateField('year', parseInt(e.target.value) || 2024)} required className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#DC2626]" data-testid="vehicle-year" />
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Kilométrage *</label>
      <input type="text" inputMode="numeric" value={form.mileage} onChange={e => updateField('mileage', e.target.value.replace(/[^0-9]/g, ''))} required className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#DC2626]" placeholder="Ex: 85000" data-testid="vehicle-mileage" />
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Carburant *</label>
      <select value={form.fuel} onChange={e => updateField('fuel', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#DC2626]" data-testid="vehicle-fuel">
        <option value="Essence">Essence</option>
        <option value="Diesel">Diesel</option>
        <option value="Hybride">Hybride</option>
        <option value="Électrique">Électrique</option>
        <option value="GPL">GPL</option>
      </select>
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Boîte de vitesse *</label>
      <select value={form.transmission} onChange={e => updateField('transmission', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#DC2626]" data-testid="vehicle-transmission">
        <option value="Manuelle">Manuelle</option>
        <option value="Automatique">Automatique</option>
      </select>
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Statut *</label>
      <select value={form.status} onChange={e => updateField('status', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#DC2626]" data-testid="vehicle-status">
        <option value="disponible">Disponible</option>
        <option value="réservé">Réservé</option>
        <option value="vendu">Vendu</option>
      </select>
    </div>
    <div className="flex items-center gap-2 pt-6">
      <input type="checkbox" id="featured" checked={form.featured} onChange={e => updateField('featured', e.target.checked)} className="w-4 h-4 text-[#DC2626] rounded" data-testid="vehicle-featured" />
      <label htmlFor="featured" className="text-sm font-medium text-gray-700">Mettre en vedette</label>
    </div>
  </>
);

const VehicleForm = ({ vehicle, onSave, onCancel, authHeaders }) => {
  const [form, setForm] = useState({
    title: vehicle?.title || '',
    brand: vehicle?.brand || '',
    model: vehicle?.model || '',
    year: vehicle?.year || new Date().getFullYear(),
    mileage: vehicle?.mileage !== undefined ? String(vehicle.mileage) : '0',
    fuel: vehicle?.fuel || 'Essence',
    transmission: vehicle?.transmission || 'Manuelle',
    price: vehicle?.price !== undefined && vehicle?.price !== 0 ? String(vehicle.price) : '',
    description: vehicle?.description || '',
    status: vehicle?.status || 'disponible',
    featured: vehicle?.featured || false,
    images: vehicle?.images || [],
    options: vehicle?.options || []
  });
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const priceNum = parseInt(form.price) || 0;
    if (priceNum <= 0) {
      setError('Veuillez entrer un prix valide.');
      return;
    }
    onSave({ ...form, price: priceNum, mileage: parseInt(form.mileage) || 0 });
  };

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-md w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">
            {vehicle ? 'Modifier le véhicule' : 'Ajouter un véhicule'}
          </h2>
          <button onClick={onCancel} className="p-2 hover:bg-gray-100 rounded"><X size={24} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="vehicle-form">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm" data-testid="vehicle-form-error">{error}</div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <BasicInfoFields form={form} updateField={updateField} />
            <SpecsFields form={form} updateField={updateField} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => updateField('description', e.target.value)} rows={4} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#DC2626] resize-none" placeholder="Description du véhicule..." data-testid="vehicle-description" />
          </div>

          <VehicleFormImages
            images={form.images}
            onChange={(images) => updateField('images', images)}
            authHeaders={authHeaders}
          />

          <VehicleFormOptions
            options={form.options}
            onChange={(options) => updateField('options', options)}
          />

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onCancel} className="flex-1 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">Annuler</button>
            <button type="submit" className="flex-1 cta-button justify-center" data-testid="save-vehicle-btn">{vehicle ? 'Enregistrer' : 'Ajouter'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VehicleForm;
